import { useState, useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import type { WorkspaceResponse } from '../api';
import { BillingPage } from './BillingPage';
import { AccountPage } from './AccountPage';
import { CapabilitiesPage } from './CapabilitiesPage';
import { IntegrationsPage } from './IntegrationsPage';
import { CloudAccountsPanel } from '../components/settings/CloudAccountsPanel';
import { AlertChannelsPanel } from '../components/settings/AlertChannelsPanel';
import { Page, PageHeader, Tabs } from '../components/primitives';
import type { TabOption } from '../components/primitives';

export type SettingsTab = 'billing' | 'account' | 'alerts' | 'cloud' | 'integrations' | 'capabilities' | 'general';

type VisibleSettingsTab = Exclude<SettingsTab, 'general'>;

const SETTINGS_TABS_ID = 'settings-tab';
const SETTINGS_PANEL_ID = 'settings-panel';
const SETTINGS_TAB_OPTIONS: TabOption<VisibleSettingsTab>[] = [
  { value: 'billing', label: 'Billing' },
  { value: 'account', label: 'Your account' },
  { value: 'alerts', label: 'Alerts' },
  { value: 'cloud', label: 'Cloud accounts' },
  { value: 'integrations', label: 'Repository access' },
  { value: 'capabilities', label: 'Platform' },
];

export function SettingsPage({ workspace }: { workspace: WorkspaceResponse }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as SettingsTab) || 'billing';
  const [tab, setTab] = useState<SettingsTab>(
    ['billing', 'account', 'alerts', 'cloud', 'integrations', 'capabilities'].includes(initialTab) ? initialTab : 'billing'
  );

  useEffect(() => {
    const urlTab = searchParams.get('tab') as SettingsTab;
    if (urlTab && ['billing', 'account', 'alerts', 'cloud', 'integrations', 'capabilities'].includes(urlTab) && urlTab !== tab) {
      setTab(urlTab);
    }
  }, [searchParams]);

  function handleTabChange(newTab: SettingsTab) {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  }

  return (
    <Page className="settings-page">
      <PageHeader title="Settings" />

      <Tabs
        label="Settings"
        variant="primary"
        idBase={SETTINGS_TABS_ID}
        panelId={SETTINGS_PANEL_ID}
        value={tab}
        onChange={handleTabChange}
        options={SETTINGS_TAB_OPTIONS}
      />

      <div
        className="settings-page__content"
        role="tabpanel"
        id={SETTINGS_PANEL_ID}
        aria-labelledby={`${SETTINGS_TABS_ID}-${tab}`}
      >
        {tab === 'billing' && <BillingPage workspace={workspace} />}
        {tab === 'account' && <AccountPage workspace={workspace} />}
        {tab === 'cloud' && <CloudAccountsPanel isOwner={Boolean(workspace.identity.role?.is_owner_role || workspace.identity.kind === 'dev_workspace')} />}
        {tab === 'alerts' && <AlertChannelsPanel isOwner={Boolean(workspace.identity.role?.is_owner_role || workspace.identity.kind === 'dev_workspace')} />}
        {tab === 'integrations' && <IntegrationsPage />}
        {tab === 'capabilities' && <CapabilitiesPage />}
      </div>
    </Page>
  );
}

/** The old stand-alone Billing, Account and Capabilities URLs open their
 * Settings tab (audit N3), keeping any other query parameter (a checkout
 * return, say). */
export function SettingsRedirect({ tab }: { tab: VisibleSettingsTab }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', tab);
  return <Navigate to={`/app/settings?${params.toString()}`} replace />;
}

